import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { Quote } from '@/lib/data-store';
import { Customer } from '@/lib/data-store';

interface QuotationFormProps {
  quote?: Quote;
  noSebutharga: string;
  customers: Customer[];
  onSubmit: (data: Quote) => void;
  onCancel: () => void;
}

export const QuotationForm = ({ quote, noSebutharga, customers, onSubmit, onCancel }: QuotationFormProps) => {
  const [formData, setFormData] = useState<Partial<Quote>>(
    quote || {
      id: Date.now().toString(),
      no: noSebutharga,
      pelanggan: '',
      namaProyek: '',
      jumlah: 0,
      tanggal: new Date().toISOString().split('T')[0],
      status: 'Draft',
      catatan: '',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'jumlah' ? parseFloat(value) || 0 : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.pelanggan?.trim()) newErrors.pelanggan = 'Pelanggan is required';
    if (!formData.namaProyek?.trim()) newErrors.namaProyek = 'Nama Projek is required';
    if (!formData.tanggal?.trim()) newErrors.tanggal = 'Tarikh is required';
    if (!formData.jumlah || formData.jumlah <= 0) newErrors.jumlah = 'Jumlah must be greater than 0';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as Quote);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Sebutharga Baru</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombor Sebutharga (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nombor Sebutharga</label>
              <Input
                type="text"
                value={formData.no || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Nombor akan di-jana secara automatik</p>
            </div>

            {/* Pelanggan - Dropdown */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Pelanggan</label>
              <select
                name="pelanggan"
                value={formData.pelanggan || ''}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.pelanggan ? 'border-red-500' : 'border-slate-300'
                }`}
              >
                <option value="">Cari atau taip nama pelanggan...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.namaSyarikat}>
                    {customer.namaSyarikat}
                  </option>
                ))}
              </select>
              {errors.pelanggan && (
                <p className="text-sm text-red-600">{errors.pelanggan}</p>
              )}
            </div>

            {/* Nama Projek */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Projek</label>
              <Input
                type="text"
                name="namaProyek"
                value={(formData as any).namaProyek || ''}
                onChange={handleChange}
                placeholder="Nama projek (pilihan)"
                className={errors.namaProyek ? 'border-red-500' : ''}
              />
              {errors.namaProyek && (
                <p className="text-sm text-red-600">{errors.namaProyek}</p>
              )}
            </div>

            {/* Jumlah (RM) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Jumlah (RM)</label>
              <Input
                type="number"
                name="jumlah"
                value={formData.jumlah || ''}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={errors.jumlah ? 'border-red-500' : ''}
              />
              {errors.jumlah && (
                <p className="text-sm text-red-600">{errors.jumlah}</p>
              )}
            </div>

            {/* Tarikh */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Tarikh</label>
              <Input
                type="date"
                name="tanggal"
                value={formData.tanggal || ''}
                onChange={handleChange}
                className={errors.tanggal ? 'border-red-500' : ''}
              />
              {errors.tanggal && (
                <p className="text-sm text-red-600">{errors.tanggal}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={formData.status || 'Draft'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Draft">Draft</option>
                <option value="Diterima">Diterima</option>
                <option value="Menunggu">Menunggu</option>
                <option value="Ditolak">Ditolak</option>
              </select>
            </div>

            {/* Catatan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Catatan</label>
              <textarea
                name="catatan"
                value={(formData as any).catatan || ''}
                onChange={handleChange}
                placeholder="Tambahkan catatan di sini..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Batal
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};